import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  OnInit,
  output,
  Output,
  signal
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { _HttpClient } from '@delon/theme';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { TransactionPreviewReceivePaymentComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-receive-payment.component';
import { TransactionPreviewPayBillComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-pay-bill.component';
import { TransactionPreviewCustomerRefundComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-customer-refund.component';
import { TransactionPreviewCreditNoteComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-credit-note.component';
import { TransactionPreviewDebitNoteComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-debit-note.component';
import { TransactionPreviewBillComponent } from '@shared-feature/components/actions/doc-edit-actions/components/transaction-preview/transaction-preview-bill.component';
import { ReportingService } from '../../../../core/util/reporting.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-send-email',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzModalModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzGridModule,
    NzAutocompleteModule,
    NzFormDirective,
    TransactionPreviewReceivePaymentComponent,
    TransactionPreviewPayBillComponent,
    TransactionPreviewCustomerRefundComponent,
    TransactionPreviewCreditNoteComponent,
    TransactionPreviewDebitNoteComponent,
    TransactionPreviewBillComponent
  ],
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendEmailComponent implements OnInit {
  private readonly defaultCompanyLogo =
    'https://qnestorage.blob.core.windows.net/cloud-accounting/Email%20Resouces/official-logo-new-colored.png';
  visible = signal(false);
  senderTransactionType = input<any>(null);
  transactionId = input<any>(null);
  readonly = input<boolean>(false);
  reportType = computed(() => this.senderTransactionType());
  emailHistoryDetail = input<any>(null);
  docType = input<any>('');
  docCode = input<any>('');
  customReportType = input<any>(null);

  reportTypeString = computed(() => {
    if (this.customReportType()) {
      return this.customReportType();
    } else {
      return this.docType() ? this.docType() + '-voucher' : null;
    }
  });

  reportUrl = signal<any>(null);
  close = output();
  @Output() emailSent = new EventEmitter<void>();
  emailForm!: FormGroup;
  loading: boolean = false;
  isBccVisible: boolean = false;
  showPreview: boolean = true;

  data: any = {
    receipientName: '',
    receipientEmail: '',
    primaryEmail: '',
    ccList: [],
    ccOptions: [],
    bcc: '',
    subject: '',
    message: '',
    companyName: '',
    email: '',
    phoneNo: '',
    regNo: '',
    // Preview related fields
    recipientBillTargetLabel: '',
    docDate: '',
    transactionType: '',
    docCode: '',
    dueDate: '',
    term: '',
    currencyCode: '',
    items: [],
    knockoffItems: [],
    accountItems: [],
    taxableAmount: 0,
    taxAmount: 0,
    netTotal: 0,
    totalAmount: 0,
    knockoffTotal: 0,
    paymentTotal: 0,
    unmatchedAmount: 0,
    accountTotal: 0
  };

  filteredEmailSuggestions: string[] = [];
  companyLogo: string = '';
  emailPreview: string = '';

  // Template keys for different transaction types
  readonly templateKeys = {
    defaultInvoice: 0,
    receivePayment: 1,
    debitNote: 2,
    creditNote: 3,
    bill: 4,
    customerRefund: 5,
    apPayBills: 6
  } as const;

  previewTemplateKey: number = this.templateKeys.defaultInvoice;

  constructor(
    private fb: FormBuilder,
    private http: _HttpClient,
    private msg: NzMessageService,
    private reporting: ReportingService,
    private notificationSrv: NzNotificationService
  ) {
    effect(() => {
      if (!!this.reportType() && !!this.transactionId()) {
        this.loadEmailData();
      }
    });

    effect(() => {
      if (this.reportTypeString()) {
        this.getReportUrl();
      }
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.loadCompanyLogo();
  }

  getReportUrl() {
    this.http
      .get(
        `${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`,
        {
          reportType: this.reportTypeString()
        }
      )
      .subscribe(res => {
        this.reportUrl.set(res?.[0].reportUrl);
      });
  }

  private initForm(): void {
    // Initialize with empty arrays - actual values will be set in loadEmailData()
    // Only use history data if in readonly mode and history exists
    let ccListArray: string[] = [];
    const emailHistory = this.emailHistoryDetail();
    if (emailHistory && this.readonly()) {
      const cc = emailHistory.cc;
      if (cc && typeof cc === 'string') {
        ccListArray = cc
          .split(',')
          .map((email: string) => email.trim())
          .filter((email: string) => email);
      }
    }
    
    let bccListArray: string[] = [];
    if (emailHistory && this.readonly()) {
      const bcc = emailHistory.bcc;
      if (bcc && typeof bcc === 'string') {
        bccListArray = bcc
          .split(',')
          .map((email: string) => email.trim())
          .filter((email: string) => email);
      }
    }
    
    this.emailForm = this.fb.group({
      receipientName: [{ value: '', disabled: true }],
      receipientEmail: [
        { value: '', disabled: true },
        [Validators.required, Validators.email]
      ],
      ccList: [{value:ccListArray, disabled: this.readonly()}],
      bccList: [{value: bccListArray, disabled: this.readonly()}],
      subject: ['', Validators.required],
      message: ['', Validators.required]
    });
  }

  private loadCompanyLogo(): void {
    this.http.get('api/companyprofile/logo?_silent=true').subscribe({
      next: (res: any) => {
        if (res && res.companyImage !== null) {
          this.companyLogo = this.normalizeImageUrl(res.companyImage);
        } else {
          this.companyLogo = this.defaultCompanyLogo;
        }
      },
      error: () => {
        this.companyLogo = this.defaultCompanyLogo;
      }
    });
  }

  private normalizeImageUrl(url?: string): string {
    if (!url) return this.defaultCompanyLogo;
    return url.replace(/ /g, '%20');
  }

  private loadEmailData(): void {
    if (!this.reportType() || !this.transactionId()) return;

    this.loading = true;
    this.http
      .post('api/transactionSender/info?_silent=true', {
        type: this.reportType(),
        transactionId: this.transactionId(),
        option: 'email'
      })
      .subscribe({
        next: (res: any) => {
          this.data = res;
          // Always use the latest primary email from the API response (latest customer email)
          this.data.primaryEmail = this.data.receipientEmail;

          // Use the latest secondary emails from the API response as available CC options
          // res.ccList contains the latest customer secondary emails
          this.data.ccOptions = res.ccList && Array.isArray(res.ccList) 
            ? [...res.ccList] 
            : [];

          // Initialize filtered suggestions with latest email options
          this.filteredEmailSuggestions = this.data.ccOptions
            ? [...this.data.ccOptions]
            : [];

          // For CC list values: 
          // - If in readonly mode (viewing sent email), use old history data
          // - Otherwise (resending), use empty array or latest secondary emails
          let ccListArray: string[] = [];
          if (this.readonly() && this.emailHistoryDetail()?.cc) {
            // In readonly mode, show the old CC values from history
            const cc = this.emailHistoryDetail().cc;
            if (typeof cc === 'string' && cc.length > 0) {
              ccListArray = cc
                .split(',')
                .map((email: string) => email.trim())
                .filter((email: string) => email);
            }
          } else {
            // When resending, start with empty CC or optionally pre-populate with latest secondary emails
            // Leave empty to let user choose
            ccListArray = [];
          }

          // For BCC list values:
          // - If in readonly mode (viewing sent email), use old history data
          // - Otherwise (resending), use empty array
          let bccListArray: string[] = [];
          if (this.readonly() && this.emailHistoryDetail()?.bcc) {
            // In readonly mode, show the old BCC values from history
            const bcc = this.emailHistoryDetail().bcc;
            if (typeof bcc === 'string' && bcc.length > 0) {
              bccListArray = bcc
                .split(',')
                .map((email: string) => email.trim())
                .filter((email: string) => email);
            }
          } else {
            // When resending, start with empty BCC
            bccListArray = [];
          }

          // Patch form values (readonly fields remain disabled)
          // In readonly mode (viewing sent email), use email history values to show what was actually sent
          // Otherwise (resending), use latest customer info from API
          const emailHistory = this.emailHistoryDetail();
          
          // For readonly mode, use email history values (what was actually sent)
          // For resending, use latest customer info from API
          const finalReceipientEmail = this.readonly() && emailHistory?.receipientEmail 
            ? emailHistory.receipientEmail 
            : (this.data.receipientEmail || '');
          const finalSubject = this.readonly() && emailHistory?.subject 
            ? emailHistory.subject 
            : (this.data.subject || '');
          const finalMessage = this.readonly() && emailHistory?.message 
            ? emailHistory.message 
            : (this.data.message || '');
          
          // Also update primaryEmail for readonly mode to match what was sent
          if (this.readonly() && emailHistory?.receipientEmail) {
            this.data.primaryEmail = emailHistory.receipientEmail;
          }
          
          this.emailForm.patchValue({
            receipientName: this.data.receipientName || '',
            receipientEmail: finalReceipientEmail,
            ccList: ccListArray,
            bccList: bccListArray,
            subject: finalSubject,
            message: finalMessage
          });

          // Set BCC visibility if BCC exists in history (readonly mode) or if user wants to add BCC
          if (this.readonly() && bccListArray.length > 0) {
            this.isBccVisible = true;
          } else if (res.bcc) {
            this.isBccVisible = true;
          }

          // Set preview template key from API response
          this.previewTemplateKey =
            this.data.templateKey ?? this.templateKeys.defaultInvoice;

          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.msg.error('Failed to load email data');
        }
      });
  }

  toggleBccField(): void {
    this.isBccVisible = !this.isBccVisible;
  }

  onEmailInput(value: string): void {
    this.updateFilteredEmailSuggestions(value);
  }

  private updateFilteredEmailSuggestions(inputValue: string): void {
    if (!this.data.ccOptions) {
      this.filteredEmailSuggestions = [];
      return;
    }

    if (!inputValue) {
      this.filteredEmailSuggestions = [...this.data.ccOptions];
      return;
    }

    this.filteredEmailSuggestions = this.data.ccOptions.filter(
      (email: string) => email.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  shouldShowPrimaryEmail(): boolean {
    if (!this.data.primaryEmail) return false;

    const ccList = this.emailForm.get('ccList')?.value || [];
    return !ccList.includes(this.data.primaryEmail);
  }

  isEmailDisabled(email: string, fieldName: string): boolean {
    if (fieldName === 'ccList') {
      return this.data.receipientEmail === email;
    } else if (fieldName === 'bccList') {
      // For BCC field, disable email that is already selected in Send To or CC
      const ccList = this.emailForm.get('ccList')?.value || [];
      return this.data.receipientEmail === email || ccList.includes(email);
    }
    return false;
  }

  hasInvalidEmail(fieldName: string): boolean {
    const control = this.emailForm.get(fieldName);
    if (fieldName === 'bccList') {
      // Validate BCC array - check if all emails are valid
      const bccList = control?.value || [];
      if (bccList.length === 0) return false; // Empty is valid
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return !bccList.every((email: string) => emailPattern.test(email.trim()));
    }
    return !!(control?.invalid && (control?.dirty || control?.touched));
  }

  handleCancel(): void {
    this.close.emit();
  }

  handleSubmit(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.emailForm.controls).forEach(key => {
      this.emailForm.get(key)?.markAsTouched();
    });

    if (this.emailForm.invalid) {
      this.msg.warning('Please fill in all required fields correctly');
      return;
    }

    const formValue = this.emailForm.getRawValue();

    const request = {
      docCode: this.docCode(),
      docType: this.docType(),
      senderName: this.data.companyName,
      senderEmail: this.data.email,
      receipientEmail: formValue.receipientEmail,
      subject: formValue.subject,
      htmlContent: '',
      message: formValue.message,
      cc: formValue.ccList.join(','),
      bcc: formValue.bccList.join(','),
      type: 1,
      filter: JSON.stringify({ selection: [this.docCode()] }),
      usePrimaryDb: true,
      reportUrl: this.reportUrl() ?? ''
    };

    this.handleCancel();

    const notification = this.notificationSrv.create(
      'info',
      'Sending email...',
      'Please wait',
      { nzDuration: 0 }
    );

    this.http
      .post(
        'api/EmailSender/SendDocument',
        request
      )
      .pipe(finalize(() => this.notificationSrv.remove(notification.messageId)))
      .subscribe({
        next: () => {
          this.emailSent.emit();
        }
      });
  }
}
